/**
 * Feedback Service
 *
 * CRUD operations for the feedback system including:
 * - Feedback requests (bugs, enhancements, questions)
 * - Comments
 * - Status history
 * - Auto-error groups (admin only)
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type {
  FeedbackRequest,
  FeedbackRequestWithDetails,
  FeedbackComment,
  FeedbackCommentWithUser,
  FeedbackStatusHistory,
  FeedbackStatus,
  AutoErrorGroup,
  AutoErrorGroupWithTicket,
  CreateFeedbackInput,
  UpdateFeedbackInput,
  CreateCommentInput,
  FeedbackFilters,
  ErrorGroupFilters,
  FeedbackMetrics,
  ErrorGroupMetrics,
  FeedbackEnvironment
} from '@/types/feedback';

const log = createComponentLogger('FeedbackService', 'database');

// =====================================================
// Storage helpers
// =====================================================

const FEEDBACK_BUCKET = 'feedback-attachments';
const SIGNED_URL_TTL_SECONDS = 3600;

/**
 * Generate a signed URL for a private feedback attachment.
 * Tolerates both new path-based attachments and legacy public-URL ones.
 */
export async function createFeedbackAttachmentSignedUrl(
  pathOrLegacy: string
): Promise<string | null> {
  if (!pathOrLegacy) {
    return null;
  }
  const path = pathOrLegacy.startsWith('http')
    ? pathOrLegacy.replace(/^.*\/object\/public\/feedback-attachments\//, '')
    : pathOrLegacy;
  if (!path) {
    return null;
  }
  const { data, error } = await supabase.storage
    .from(FEEDBACK_BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
  if (error) {
    log.warn('Signed URL failed', { path, error: error.message });
    return null;
  }
  return data?.signedUrl ?? null;
}

// =====================================================
// Feedback Requests
// =====================================================

/**
 * Create a new feedback request
 */
export async function createFeedback(
  input: CreateFeedbackInput,
  environment?: FeedbackEnvironment
): Promise<FeedbackRequest> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User must be authenticated to submit feedback');
  }

  log.info('Creating feedback request', { type: input.type, category: input.category });

  const { data, error } = await supabase
    .from('feedback_requests')
    .insert({
      title: input.title,
      type: input.type,
      description: input.description,
      category: input.category,
      severity: input.severity || null,
      steps_to_reproduce: input.steps_to_reproduce || null,
      expected_behavior: input.expected_behavior || null,
      actual_behavior: input.actual_behavior || null,
      environment: environment || {},
      attachments: input.attachments || [],
      submitted_by: user.id,
      source: 'manual'
    })
    .select()
    .single();

  if (error) {
    log.error('Failed to create feedback', { error: error.message });
    throw error;
  }

  log.info('Feedback created successfully', { ticketId: data.ticket_id });
  return data as FeedbackRequest;
}

/**
 * Get a single feedback request by ID
 */
export async function getFeedbackById(id: string): Promise<FeedbackRequestWithDetails | null> {
  // Note: cannot embed users!submitted_by / users!assigned_to — feedback_requests
  // references auth.users, not public.users, so PostgREST can't resolve those FKs.
  // Submitter/assignee are surfaced separately via the resolve-feedback path.
  const { data, error } = await supabase
    .from('feedback_requests')
    .select(`
      *,
      error_group:auto_error_groups!error_group_id(*),
      duplicate_request:feedback_requests!duplicate_of(id, ticket_id, title)
    `)
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    log.error('Failed to get feedback', { id, error: error.message });
    throw error;
  }

  // Get comment count
  const { count } = await supabase
    .from('feedback_comments')
    .select('*', { count: 'exact', head: true })
    .eq('feedback_id', id);

  return {
    ...data,
    comments_count: count || 0
  } as FeedbackRequestWithDetails;
}

/**
 * Get feedback requests with filters
 */
export async function getFeedbackList(
  filters: FeedbackFilters = {},
  limit = 50,
  offset = 0
): Promise<{ data: FeedbackRequestWithDetails[]; count: number }> {
  // Note: Cannot join to users table because feedback_requests references auth.users, not public.users
  // User info will need to be fetched separately or displayed as UUID
  let query = supabase
    .from('feedback_requests')
    .select('*', { count: 'exact' });

  // Apply filters
  if (filters.status && filters.status.length > 0) {
    query = query.in('status', filters.status);
  }

  if (filters.type && filters.type.length > 0) {
    query = query.in('type', filters.type);
  }

  if (filters.priority && filters.priority.length > 0) {
    query = query.in('priority', filters.priority);
  }

  if (filters.category && filters.category.length > 0) {
    query = query.in('category', filters.category);
  }

  if (filters.source && filters.source.length > 0) {
    query = query.in('source', filters.source);
  }

  if (filters.assigned_to) {
    query = query.eq('assigned_to', filters.assigned_to);
  }

  if (filters.submitted_by) {
    query = query.eq('submitted_by', filters.submitted_by);
  }

  if (filters.search) {
    query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%,ticket_id.ilike.%${filters.search}%`);
  }

  if (filters.date_from) {
    query = query.gte('created_at', filters.date_from);
  }

  if (filters.date_to) {
    query = query.lte('created_at', filters.date_to);
  }

  // Order and pagination
  query = query
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  const { data, error, count } = await query;

  if (error) {
    log.error('Failed to get feedback list', { error: error.message });
    throw error;
  }

  return {
    data: (data || []) as FeedbackRequestWithDetails[],
    count: count || 0
  };
}

/**
 * Get current user's feedback requests
 */
export async function getMyFeedback(
  limit = 50,
  offset = 0
): Promise<{ data: FeedbackRequest[]; count: number }> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User must be authenticated');
  }

  const { data, error, count } = await supabase
    .from('feedback_requests')
    .select('*', { count: 'exact' })
    .eq('submitted_by', user.id)
    .eq('source', 'manual') // Don't show auto-detected errors to users
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) {
    log.error('Failed to get user feedback', { error: error.message });
    throw error;
  }

  return {
    data: (data || []) as FeedbackRequest[],
    count: count || 0
  };
}

/**
 * Update a feedback request (admin only)
 */
export async function updateFeedback(
  id: string,
  input: UpdateFeedbackInput
): Promise<FeedbackRequest> {
  log.info('Updating feedback', { id, updates: Object.keys(input) });

  const updateData: Record<string, unknown> = {};

  if (input.title !== undefined) {
updateData.title = input.title;
}
  if (input.description !== undefined) {
updateData.description = input.description;
}
  if (input.category !== undefined) {
updateData.category = input.category;
}
  if (input.severity !== undefined) {
updateData.severity = input.severity || null;
}
  if (input.steps_to_reproduce !== undefined) {
updateData.steps_to_reproduce = input.steps_to_reproduce || null;
}
  if (input.expected_behavior !== undefined) {
updateData.expected_behavior = input.expected_behavior || null;
}
  if (input.actual_behavior !== undefined) {
updateData.actual_behavior = input.actual_behavior || null;
}
  if (input.status !== undefined) {
updateData.status = input.status;
}
  if (input.priority !== undefined) {
updateData.priority = input.priority || null;
}
  if (input.assigned_to !== undefined) {
updateData.assigned_to = input.assigned_to || null;
}
  if (input.resolution !== undefined) {
updateData.resolution = input.resolution || null;
}
  if (input.duplicate_of !== undefined) {
updateData.duplicate_of = input.duplicate_of || null;
}

  const { data, error } = await supabase
    .from('feedback_requests')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    log.error('Failed to update feedback', { id, error: error.message });
    throw error;
  }

  log.info('Feedback updated successfully', { id, ticketId: data.ticket_id });
  return data as FeedbackRequest;
}

/**
 * Delete a feedback request (admin only)
 */
export async function deleteFeedback(id: string): Promise<void> {
  log.info('Deleting feedback', { id });

  const { error } = await supabase
    .from('feedback_requests')
    .delete()
    .eq('id', id);

  if (error) {
    log.error('Failed to delete feedback', { id, error: error.message });
    throw error;
  }

  log.info('Feedback deleted successfully', { id });
}

/**
 * Update feedback status (convenience function for admin)
 */
export async function updateFeedbackStatus(
  id: string,
  status: FeedbackStatus
): Promise<FeedbackRequest> {
  return updateFeedback(id, { status });
}

/**
 * Resolve a feedback ticket: server-side flow that updates status to
 * 'resolved' with notes, then sends a one-shot email to the submitter
 * via Resend (idempotent via resolved_notification_sent_at).
 *
 * Returns:
 *   - { ok: true, ticketId, emailedTo }                 → resolved + emailed
 *   - { ok: true, skipped: 'no_op' | 'already_emailed' } → already done previously
 *   - { ok: true, emailFailed: true, error }            → status saved, email failed
 */
export interface ResolveFeedbackResult {
  ok?: boolean;
  ticketId?: string;
  emailedTo?: string;
  skipped?: 'no_op' | 'already_emailed';
  emailFailed?: boolean;
  /** Set when the email was sent but the idempotency stamp failed. */
  stampWarning?: 'idempotency_stamp_failed';
  error?: string;
}

export async function resolveFeedback(
  id: string,
  resolution: string
): Promise<ResolveFeedbackResult> {
  const { data, error } = await supabase.functions.invoke('resolve-feedback', {
    body: { feedbackId: id, resolution }
  });
  if (error) {
    log.error('resolve-feedback invoke failed', { id }, error);
    throw error;
  }
  return (data ?? {}) as ResolveFeedbackResult;
}

// =====================================================
// Comments
// =====================================================

/**
 * Get comments for a feedback request
 */
export async function getComments(feedbackId: string): Promise<FeedbackCommentWithUser[]> {
  const { data, error } = await supabase
    .from('feedback_comments')
    .select(`
      *,
      user:users!user_id(id, email, first_name, last_name, avatar_url)
    `)
    .eq('feedback_id', feedbackId)
    .order('created_at', { ascending: true });

  if (error) {
    log.error('Failed to get comments', { feedbackId, error: error.message });
    throw error;
  }

  return (data || []) as FeedbackCommentWithUser[];
}

/**
 * Add a comment to a feedback request
 */
export async function addComment(input: CreateCommentInput): Promise<FeedbackComment> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('User must be authenticated to add comments');
  }

  log.info('Adding comment', { feedbackId: input.feedback_id });

  const { data, error } = await supabase
    .from('feedback_comments')
    .insert({
      feedback_id: input.feedback_id,
      user_id: user.id,
      content: input.content,
      is_internal: input.is_internal || false
    })
    .select()
    .single();

  if (error) {
    log.error('Failed to add comment', { error: error.message });
    throw error;
  }

  log.info('Comment added successfully', { commentId: data.id });
  return data as FeedbackComment;
}

// =====================================================
// Status History
// =====================================================

/**
 * Get status history for a feedback request
 */
export async function getStatusHistory(feedbackId: string): Promise<FeedbackStatusHistory[]> {
  const { data, error } = await supabase
    .from('feedback_status_history')
    .select(`
      *,
      changed_by_user:users!changed_by(id, email, first_name, last_name)
    `)
    .eq('feedback_id', feedbackId)
    .order('created_at', { ascending: false });

  if (error) {
    log.error('Failed to get status history', { feedbackId, error: error.message });
    throw error;
  }

  return (data || []) as FeedbackStatusHistory[];
}

// =====================================================
// Auto Error Groups (Admin Only)
// =====================================================

/**
 * Get auto-detected error groups
 */
export async function getErrorGroups(
  filters: ErrorGroupFilters = {},
  limit = 50,
  offset = 0
): Promise<{ data: AutoErrorGroupWithTicket[]; count: number }> {
  let query = supabase
    .from('auto_error_groups')
    .select(`
      *,
      feedback_request:feedback_requests!feedback_id(id, ticket_id, title, status)
    `, { count: 'exact' });

  // Apply filters
  if (filters.error_type && filters.error_type.length > 0) {
    query = query.in('error_type', filters.error_type);
  }

  if (filters.is_resolved !== undefined) {
    query = query.eq('is_resolved', filters.is_resolved);
  }

  if (filters.has_ticket !== undefined) {
    if (filters.has_ticket) {
      query = query.not('feedback_id', 'is', null);
    } else {
      query = query.is('feedback_id', null);
    }
  }

  if (filters.search) {
    query = query.ilike('error_message', `%${filters.search}%`);
  }

  if (filters.date_from) {
    query = query.gte('first_seen_at', filters.date_from);
  }

  if (filters.date_to) {
    query = query.lte('last_seen_at', filters.date_to);
  }

  // Order by occurrence count (most frequent first)
  query = query
    .order('occurrence_count', { ascending: false })
    .range(offset, offset + limit - 1);

  const { data, error, count } = await query;

  if (error) {
    log.error('Failed to get error groups', { error: error.message });
    throw error;
  }

  return {
    data: (data || []) as AutoErrorGroupWithTicket[],
    count: count || 0
  };
}

/**
 * Mark an error group as resolved
 */
export async function resolveErrorGroup(id: string): Promise<AutoErrorGroup> {
  log.info('Resolving error group', { id });

  const { data, error } = await supabase
    .from('auto_error_groups')
    .update({ is_resolved: true })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    log.error('Failed to resolve error group', { id, error: error.message });
    throw error;
  }

  log.info('Error group resolved', { id });
  return data as AutoErrorGroup;
}

// =====================================================
// Metrics
// =====================================================

/**
 * Get feedback metrics for dashboard
 */
export async function getFeedbackMetrics(): Promise<FeedbackMetrics> {
  // Get counts by status
  const { data: byStatus } = await supabase
    .from('feedback_requests')
    .select('status')
    .eq('source', 'manual');

  // Get counts by type
  const { data: byType } = await supabase
    .from('feedback_requests')
    .select('type')
    .eq('source', 'manual');

  // Get counts by priority
  const { data: byPriority } = await supabase
    .from('feedback_requests')
    .select('priority')
    .eq('source', 'manual')
    .not('priority', 'is', null);

  // Get counts by source
  const { data: bySource } = await supabase
    .from('feedback_requests')
    .select('source');

  // Calculate metrics
  const statusCounts = (byStatus || []).reduce((acc, row) => {
    acc[row.status] = (acc[row.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const typeCounts = (byType || []).reduce((acc, row) => {
    acc[row.type] = (acc[row.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const priorityCounts = (byPriority || []).reduce((acc, row) => {
    if (row.priority) {
      acc[row.priority] = (acc[row.priority] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  const sourceCounts = (bySource || []).reduce((acc, row) => {
    acc[row.source] = (acc[row.source] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Open counts (not resolved, closed, wont_fix, or duplicate)
  const openStatuses = ['submitted', 'triaged', 'planned', 'in_progress'];
  const openBugs = (byStatus || []).filter(
    r => r.status && openStatuses.includes(r.status) && byType?.find(t => t.type === 'bug')
  ).length;

  const openEnhancements = (byType || []).filter(r => r.type === 'enhancement').length -
    (byStatus || []).filter(r => ['resolved', 'closed', 'wont_fix', 'duplicate'].includes(r.status)).length;

  return {
    total: (byStatus || []).length,
    by_status: statusCounts as Record<string, number>,
    by_type: typeCounts as Record<string, number>,
    by_priority: priorityCounts as Record<string, number>,
    by_source: sourceCounts as Record<string, number>,
    open_bugs: Math.max(0, openBugs),
    open_enhancements: Math.max(0, openEnhancements),
    critical_priority: priorityCounts['critical'] || 0,
    avg_resolution_time_hours: null // Would need more complex query
  };
}

/**
 * Get error group metrics for dashboard
 */
export async function getErrorGroupMetrics(): Promise<ErrorGroupMetrics> {
  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  // Get all error groups
  const { data: groups } = await supabase
    .from('auto_error_groups')
    .select('*');

  // Get counts by type
  const byType = (groups || []).reduce((acc, row) => {
    acc[row.error_type] = (acc[row.error_type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Calculate unique affected users
  const allUsers = new Set<string>();
  (groups || []).forEach(g => {
    (g.affected_users || []).forEach((u: string) => allUsers.add(u));
  });

  // Count last 24h
  const last24h = (groups || []).filter(g => new Date(g.last_seen_at) > yesterday).length;

  return {
    total_groups: (groups || []).length,
    total_occurrences: (groups || []).reduce((sum, g) => sum + (g.occurrence_count || 0), 0),
    unique_users_affected: allUsers.size,
    by_type: byType as Record<string, number>,
    unresolved_count: (groups || []).filter(g => !g.is_resolved).length,
    last_24h_count: last24h
  };
}

// =====================================================
// Search
// =====================================================

/**
 * Search for duplicate feedback by title similarity
 */
export async function searchDuplicates(
  title: string,
  excludeId?: string
): Promise<FeedbackRequest[]> {
  let query = supabase
    .from('feedback_requests')
    .select('*')
    .ilike('title', `%${title.split(' ').slice(0, 3).join('%')}%`)
    .eq('source', 'manual')
    .limit(5);

  if (excludeId) {
    query = query.neq('id', excludeId);
  }

  const { data, error } = await query;

  if (error) {
    log.error('Failed to search duplicates', { error: error.message });
    throw error;
  }

  return (data || []) as FeedbackRequest[];
}

/**
 * Get admin users for assignment dropdown
 */
export async function getAdminUsers(): Promise<Array<{ id: string; email: string; full_name: string }>> {
  const { data, error } = await supabase
    .from('users')
    .select('id, email, full_name')
    .contains('roles', ['admin'])
    .order('full_name');

  if (error) {
    log.error('Failed to get admin users', { error: error.message });
    throw error;
  }

  return data || [];
}
